// React-related imports
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Contexts and Hooks
import { MappingContext } from '../../contexts/MappingContext';
import { useGeneralizationAuthoring } from './hooks/useAuthoring';
import { GeneralizationContext } from '../../contexts/GeneralizationContext';
import { useSourceFiles } from '../../hooks/useSourceFiles';
import { useQuickFill } from '../../hooks/generalizationHooks/useQuickFill';
import useFullscreen from '../../hooks/useFullscreen';
import useModal from '../../hooks/useModal';
import { useCatalog } from '../../services/api';
import { useOfficeService } from '../../services/api/officeService/useOfficeService';

// Components
import LoadingOverlay from '../../components/elements/LoadingOverlay';
import SuggestionNavigateButtons from '../../components/SuggestionNavigateButtons';
import TinyEditor from '../../components/TinyEditor';
import FileNameModal from '../../components/modals/FileNameModal';
import { ImageTagging } from './components/ImageTagging';
import Layout from '../../components/layout';
import AuthoringHeader from './components/AuthoringHeader/AuthoringHeader';
import InfoPanel from '../../components/InfoPanel/InfoPanel';
import AuthoringActions from './components/AuthoringActions/AuthoringActions';
import SourcesActions from '../../components/SourceActions/SourceActions';
import QuickFillModal from '../../components/modals/QuickFillModal';
import CommentsSlideOver from './components/CommentsSlideOver/CommentsSlideOver';
import { Button, Dropdown } from '@GxT-Team/gxt-components';
import ToggleSwitch from '../../components/elements/ToggleSwitch';
import SaveChangesModal from '../../components/modals/SaveChangesModal';
import { DeleteConfirmationDialogue } from '../../components/modals/DeleteConfirmationDialogue';

// Helpers
import {
  deepCopyDocument,
  getElement,
  handleEnterKeyPress,
  isCopyColumnOrRowCommand,
  isEditorClipboardCopyShortcut,
  setAttributesAndOrder,
  getElementsByQuery,
  handleAcceptButtonClick,
  updateNewNodeAttributes,
  getPreviousNode,
} from '../../helpers/generalizationHelpers';
import { handleDownload } from '../../helpers/convertFileType';

// Constants
import { LIGHT_BLUE_COLOR, SearchThreshold } from '../../constants';

// Types
import {
  EditorEvent,
  Node as customNode,
  SearchOptions,
  SearchResult,
  EditorTypeEnum,
  AIResponse,
  EditorRefObject,
  KeyboardEventWithShift,
  MappingTypeEnum,
  AcceptanceMethodEnum,
} from '../../types';
import { AdditionalTargetSaveState, TargetSaveState } from './hooks/types';
import { OptionType } from './types';

// APIs
import { search } from '../../services/apiCalls';

//Constants
import { actionOptions, exportOptions, groupedOptions } from './utils/constants/actions';
import { AddSourcesPreviousAction } from '../catalog/util/constants';

// Other dependencies
import { Editor as TinyMCEEditor } from 'tinymce';
import _, { isEmpty, map } from 'lodash';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import NewSuggestionModal from './components/modals/NewSuggestionModal';
import { useMutationState } from '@tanstack/react-query';
import FindFigureModal from './components/modals/FindFigureModal';
import { MappingSessionProvider, useFeatureFlag } from '../../contexts';
import { EditorMode, MappingSessionStore } from '../../stores/MappingSessionStore';
import { SmartEditor, SmartEditorType } from '../../components/SmartEditor';
import { FeatureFlag } from '../../stores';
import { Observer } from 'mobx-react';
import { reaction } from 'mobx';
import useClientInfo from '../../hooks/useClientInfo';
import TinyMceModal from '../catalog/components/Modal/TinyMceModal/TinyMceModal';
import { DropDownOption } from '@GxT-Team/gxt-components/dist/components/dropdown/Dropdown';
import { SourceVersion } from '../../hooks/types';

type AiSuggestionEntryData = {
  sourceFileId?: number | string;
  value?: string;
  order?: number;
  groupingVariables?: string[][];
};

type AiSuggestionListItem = Record<string, AiSuggestionEntryData>;

type TargetEditorEventHandler = {
  event: string;
  handler: (e: KeyboardEvent | EditorEvent | KeyboardEventWithShift, editor: TinyMCEEditor) => void;
};

// Add this constant at the top with other constants
// const AUTOSAVE_DELAY = 10000; // 10 seconds in milliseconds

const Authoring = () => {
  const hasScrolledToFirstHighlight = useRef(false);
  const { editor1Ref, editor2Ref, targetId, mappingComments, editor1Content } =
    useContext(MappingContext);

  const scrollTargetToCommentNode = useCallback(
    (targetNodeId: string) => {
      const el = getElement(editor1Ref, targetNodeId);
      const node = (el?.closest('td, th') as HTMLElement | null) ?? el;
      node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    [editor1Ref],
  );
  const { toggleFullScreen } = useFullscreen();

  const {
    generatedDocument,
    loadingSourceDocument,
    sourceDocuments,
    selectedCatalogId,
    sessionId,
    mappingType,
    setSearchTerm,
    setAiResponse,
    lockSourceFile,
    gvCategories,
    aiResponse,
    targetNodeIdSuggestionMap,
    resetStates,
    lastSaved,
    authors,
    isFullScreen,
    setInitialTarget,
    imageSuggestions,
    imageSuggestionsFiles,
    sessionStatus,
    acceptedSuggestions,
    mappingSessionData,
  } = useContext(GeneralizationContext);

  const { userId, firstName, lastName } = useClientInfo();
  const { isEnabled } = useFeatureFlag();
  const mappingSessionStoreRef = useRef<MappingSessionStore>();
  if (!mappingSessionStoreRef.current) {
    mappingSessionStoreRef.current = new MappingSessionStore({
      userId,
      username: `${firstName} ${lastName}`,
    });
  }
  const mappingSessionStore = mappingSessionStoreRef.current;

  const {
    isLoading,
    highlightAllTargetSuggestions,
    highlightAllSourceDocumentSuggestions,
    receiveSuggestions,
    isUndoAvailable,
    handleGeneralizationAuthoringUndo,
    targetOnClickHandler,
    sourceDocumentOnClickHandler,
    aiSuggestionList,
    isDisabledApprove,
    handleKeyDown,
    handleKeyUp,
    setSearchResult,
    setIsSearched,
    disableLinkButton,
    handleLinkNodes,
    isNextAuthoringCellAvailable,
    isPreviousAuthoringCellAvailable,
    navigateToAuthoringCell,
    getCurrentNodeId,
    handleReset,
    isSnappingEnabled,
    updatedAiResponse,
    setCopiedAIResponse,
    handleTargetNodeIds,
    handleLockedFileChange,
    isSavingSuggestions,
    currentTargetNodeIds,
    isHeaderCellSelected,
    callSaveSuggestions,
    setColors,
    isSaving,
    handleSave,
    handleAiDropDownChange,
    // suggestionsWithColors,
    toggleImageTagging,
    handleClosePinningSlideOver,
    isTaggingSlideOverOpen,
    handleSubmitTag,
    imageTagFormValues,
    targetChangesStatus,
    openCommentModalWithThread,
    commentModal,
    displayCommentButton,
    isNewSuggestionsActive,
    setIsNewSuggestionsActive,
    setTargetChangesStatus,
    autoFillHandler,
    isImageNodeSelected,
    handleSourceNodeIds,
    isUndoEnabled,
    handleEditorChange,
    handleDuplicateTable,
    isDuplicateTableEnabled,
    handleTransferTable,
    isTransferTableEnabled,
    hasPendingAuthoringSave,
    activeUsers,
    handleNewSmartSuggestion,
    baseTargetFileId,
    setIsAutofill,
  } = useGeneralizationAuthoring({ mappingSessionStore });

  useEffect(() => {
    if (!isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR)) return;
    const dispose = reaction(
      () => ({
        target: [...(mappingSessionStore.activeNodeIds.get('target') ?? [])],
        source: [...(mappingSessionStore.activeNodeIds.get('source') ?? [])],
      }),
      ({ target, source }) => {
        handleTargetNodeIds(target);
        handleSourceNodeIds(source);
      },
      { fireImmediately: true },
    );
    return dispose;
  }, [mappingSessionStore, handleTargetNodeIds, handleSourceNodeIds, isEnabled]);

  const {
    selectedOption,
    handleOptionChange,
    clearSelection,
    handleApplyFilter,
    isQuickFillModalOpen,
    closeQuickFillModal,
    openQuickFillModal,
    isSavingFilledSuggestions,
  } = useQuickFill({
    currentTargetNodeIds: currentTargetNodeIds,
    getCurrentNodeId,
    updatedAiResponse,
    callSaveSuggestions,
    setInitialTarget,
    editor1Ref,
    aiResponse,
    sessionId,
    targetNodeIdSuggestionMap,
  });

  const {
    showSourceDeleteModal,
    handleRemoveSourceFile,
    handleFileRemoveModalClose,
    sourceDocsArray,
    sourceFilesDropdown,
    lockedFileSwitch,
    handleSourceToggle,
    currentFileVersion,
    updateDropdownSelection,
  } = useSourceFiles({ handleLockedFileChange });

  const {
    show: showSaveChangesModal,
    closeModal: closeSaveChangesModal,
    openModal: openSaveChangesModal,
  } = useModal();
  const {
    show: showCommentsSlideOver,
    closeModal: closeCommentsSlideOver,
    openModal: openCommentsSlideOver,
  } = useModal();
  const {
    show: newSuggestionsModal,
    closeModal: closeNewSuggestionsModal,
    openModal: openNewSuggestionsModal,
  } = useModal();

  const {
    show: isFindFigureModalOpen,
    closeModal: closeFindFigureModal,
    openModal: openFindFigureModal,
  } = useModal();

  const {
    show: newOrignalTemplateModal,
    closeModal: closeOrignalTemplateModal,
    openModal: openOrignalTemplateModal,
  } = useModal();

  const navigate = useNavigate();
  const location = useLocation();
  const { targetNodeId } = location.state || {};

  const [searchOptions, setSearchOptions] = useState<SearchOptions[]>([]);
  const [resetPlaceholder, setResetPlaceholder] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isSaveButtonLoading, setIsSaveButtonLoading] = useState(false);
  // Set to true before intentional programmatic navigations so the unmount cleanup skips autosave.
  const unmountSavedRef = useRef(false);
  // Tracks latest targetChangesStatus for use in cleanup (avoids stale closure).
  const targetChangesStatusRef = useRef(targetChangesStatus);
  // Tracks latest handleSmartSaveChanges for use in cleanup (defined later in file).
  const handleSmartSaveRef = useRef<
    ((editorType: SmartEditorType, acceptanceMethod: AcceptanceMethodEnum) => void) | null
  >(null);
  const updatedAiResponseRef = useRef<AIResponse | null>();
  const acceptButtonRef = useRef<HTMLButtonElement>(null);
  const undoButtonRef = useRef<HTMLButtonElement>(null);
  const generatedDocumentRef = useRef<typeof generatedDocument | null>(generatedDocument);
  const isHeaderCellSelectedRef = useRef<boolean>(false);
  const mappingTypeRef = useRef<MappingTypeEnum | null>();
  const acceptedSuggestionsRef = useRef<typeof acceptedSuggestions>(acceptedSuggestions);
  const isUndoEnabledRef = useRef<boolean>(false);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);

  const dynamicSuggestionsStatusData = useMutationState({
    filters: { mutationKey: ['dynamicMappingSuggestions'] },
    select: (mutation) => mutation.state.status,
  });

  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      mappingSessionStoreRef.current?.dispose();
    };
  }, []); // Empty

  useEffect(() => {
    if (!mappingSessionData) return;

    if (isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR)) {
      mappingSessionStore.setupData(mappingSessionData);
    }
  }, [mappingSessionData, mappingSessionStore, isEnabled]);

  useEffect(() => {
    if (!isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR)) {
      return;
    }
    mappingSessionStore.setLockSourceDocumentSelection(lockSourceFile);
  }, [lockSourceFile, mappingSessionStore, isEnabled]);

  useEffect(() => {
    const editorContent = editor1Content.html;
    if (isEmpty(editorContent) || editorContent === mappingSessionStore.activeContent.get('target'))
      return;
    mappingSessionStore.setActiveContent('target', editorContent);
  }, [editor1Content.html, mappingSessionStore]);

  const { getFileFromS3 } = useCatalog();
  const { exportCleanDocument } = useOfficeService();

  const handlePreviewDocument = (id: number | undefined) => {
    setSelectedId(id);
    openOrignalTemplateModal();
  };

  useEffect(() => {
    if (dynamicSuggestionsStatusData.includes('pending')) {
      setTargetChangesStatus(AdditionalTargetSaveState.PROCESSING);
    } else if (dynamicSuggestionsStatusData.length) {
      setTargetChangesStatus(AdditionalTargetSaveState.PROCESSED);
    }
  }, [dynamicSuggestionsStatusData, setTargetChangesStatus]);

  useEffect(() => {
    if (targetNodeId) {
      handleTargetNodeIds([targetNodeId]);
    }
  }, [targetNodeId, handleTargetNodeIds]);

  const [newSuggestionsVisited, setNewSuggestionsVisited] = useLocalStorage(
    'IsNewSuggestionsVisited',
    false,
  );

  const isAutoMapping = mappingType === MappingTypeEnum.AUTO_MAPPING;
  const isAiAuthoring = mappingType === MappingTypeEnum.AI_AUTHORING;

  const authoringSourceToolbar =
    'bold italic underline | backcolor tablecellbackgroundcolor | searchreplace | alignleft aligncenter alignright alignjustify | table | charmap';
  const authoringTargetToolbar =
    'undo | bold italic underline strikethrough backcolor tablecellbackgroundcolor | searchreplace | blocks | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist | forecolor removeformat | table | pagebreak | charmap | fullscreen preview save print | ltr rtl | tableofcontents typography formatpainter footnotes permanentpen';

  useEffect(() => {
    generatedDocumentRef.current = generatedDocument;
    mappingTypeRef.current = mappingType;
    updatedAiResponseRef.current = updatedAiResponse;
    isHeaderCellSelectedRef.current = isHeaderCellSelected;
    acceptedSuggestionsRef.current = acceptedSuggestions;

    return () => {
      generatedDocumentRef.current = null;
      mappingTypeRef.current = null;
      updatedAiResponseRef.current = null;
      isHeaderCellSelectedRef.current = false;
      acceptedSuggestionsRef.current = undefined;
    };
  }, [
    generatedDocument,
    mappingType,
    updatedAiResponse,
    isHeaderCellSelected,
    acceptedSuggestions,
  ]);

  useEffect(() => {
    return () => {
      resetStates();
    };
  }, [resetStates]);

  useEffect(() => {
    isUndoEnabledRef.current = isUndoEnabled;
  }, [isUndoEnabled]);

  const handleKeydown = (
    e: KeyboardEvent,
    acceptButtonRef: React.RefObject<HTMLButtonElement>,
    editorRef: EditorRefObject,
    isSource = false,
  ) => {
    if (isSource) {
      if (e.shiftKey && e.key === 'Enter') handleAcceptButtonClick(e, acceptButtonRef);
      // Source is read-only in transfer mode: allow copy/select-all, block typing and paste.
      if (!isEditorClipboardCopyShortcut(e)) {
        e.preventDefault();
      }
    } else {
      handleEnterKeyPress(e, acceptButtonRef, editorRef.current ?? undefined);
    }

    customUndoCallback(e);
    handleKeyDown(e);
  };

  const acceptSuggestionHandler = () => {
    if (!isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR)) {
      // Legacy flow
      receiveSuggestions(undefined, AcceptanceMethodEnum.AUTHOR).then(() => {
        if (isSnappingEnabled) navigateCells('next');
      });
      return;
    }

    // Smart editor flow
    /* TODO: refactor to use store method */
    const targetNodeIds = mappingSessionStore.activeNodeIds.get('target') ?? [];
    const targetSuggestions = map(targetNodeIds, (targetNodeId) =>
      mappingSessionStore.getSuggestionByTargetId(targetNodeId),
    );
    const targetElements = mappingSessionStore.getElementsFromContent('target');
    const sourceElements = mappingSessionStore.getElementsFromContent('source');
    const sourceFileId = mappingSessionStore.sourceFileId;

    if (!sourceElements || !targetElements || !sourceFileId) return;

    mappingSessionStore.setIsLoading(true);

    handleNewSmartSuggestion(
      targetElements,
      sourceElements,
      String(sourceFileId),
      AcceptanceMethodEnum.AUTHOR,
      targetSuggestions,
      mappingSessionStore.getSelectedContent(),
      mappingSessionStore.getSelectedRange(),
    ).finally(() => {
      mappingSessionStore.setIsLoading(false);
    });
  };

  const setMergedCellAttributesWhenNeeded = (e: EditorEvent, editor: TinyMCEEditor) => {
    if (e.command === 'mceTableMergeCells') {
      const currentCell = editor.selection.getNode().closest('td, th');
      if (currentCell) {
        currentCell.setAttribute('data-merged-cell', 'true');
      }
    }
  };

  const handleExecCommand = (
    e: EditorEvent,
    editor: TinyMCEEditor | null,
    generatedDocumentRef: React.RefObject<typeof generatedDocument>,
  ) => {
    if (!editor?.iframeElement) return;
    const iframe = editor?.iframeElement;
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (iframeDoc && generatedDocumentRef?.current?.id) {
      const nodes = iframeDoc.body.querySelectorAll<HTMLElement>(
        ":not([data-nodeid]):not(br):not(span[data-highlight]):not(tbody):not([data-mce-bogus='all']):not([data-comment='true'])",
      );
      const generatedDocumentId = generatedDocumentRef?.current?.id.toString();
      updateNewNodeAttributes(Array.from(nodes), generatedDocumentId);

      // Array.from(nodes).forEach((node) => {
      //   const matchingElements = node.querySelectorAll('[data-nodeid]:not([data-newnode])');
      //   (Array.from(matchingElements) as HTMLElement[]).forEach((ele: HTMLElement) => ele.remove());
      // });
      if (
        isCopyColumnOrRowCommand(nodes) &&
        mappingTypeRef.current === MappingTypeEnum.AI_AUTHORING
      ) {
        const nodeName = nodes[0].nodeName as 'TH' | 'TR';
        copySuggestionsToNextColumn(nodes, nodeName);
      }
      if (nodes.length === 1 && nodes[0].getAttribute('data-new-paragraph')) {
        const newParagraphNodeId = nodes[0].getAttribute('data-nodeid');
        newParagraphNodeId && handleTargetNodeIds([newParagraphNodeId]);
      }

      if (e.command === 'mceToggleFormat') {
        const node = editor.selection.getStart();
        node.setAttribute('data-newnode', 'true');
        const setNewNodeRecursively = (element: HTMLElement) => {
          element.setAttribute('data-newnode', 'true');
          Array.from(element.children).forEach((child) => {
            setNewNodeRecursively(child as HTMLElement);
          });
        };
        Array.from(node.children).forEach((child) => {
          setNewNodeRecursively(child as HTMLElement);
        });
      }
    }
    return;
  };

  const copySuggestionsToNextColumn = (nodes: NodeListOf<Element>, nodeName: 'TH' | 'TR') => {
    const copiedSuggestions: AIResponse = Array.from(nodes).reduce(
      (acc: AIResponse, node: Element) => {
        const previousNode = getPreviousNode(node, nodeName);
        const previousNodeId = previousNode?.getAttribute('data-nodeid');
        const nodeId = node.getAttribute('data-nodeid');
        if (nodeId && previousNodeId && updatedAiResponseRef && updatedAiResponseRef.current) {
          const suggestedNodeId = getCurrentNodeId(
            previousNodeId,
            editor1Ref,
            updatedAiResponseRef.current,
          );

          if (suggestedNodeId && updatedAiResponseRef.current[suggestedNodeId]) {
            // Check if this suggestion was accepted with mapperType 'author'
            const isAuthorSuggestion = acceptedSuggestionsRef.current?.some(
              (suggestion) =>
                suggestion.targetNodeId === suggestedNodeId && suggestion.mapperType === 'author',
            );

            // Only include if it's not an author suggestion
            if (!isAuthorSuggestion) {
              return { ...acc, [nodeId]: updatedAiResponseRef.current[suggestedNodeId] };
            }
          }
        }
        return acc;
      },
      {},
    );
    setAiResponse((prev) => ({ ...prev, ...copiedSuggestions }));
    setCopiedAIResponse((prev) => ({ ...prev, ...copiedSuggestions }));
  };

  const options = useCallback(
    (suggestionList: AiSuggestionListItem[], status: string) => {
      const aiSuggestions: SearchOptions[] = suggestionList?.map((s, i) => {
        const suggestions = _.get(_.values(s), '[0]') as AiSuggestionEntryData | undefined;
        const filename = sourceDocuments?.get(
          suggestions?.sourceFileId?.toString() ?? '',
        )?.filename;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = suggestions?.value ?? '';
        return {
          label: (
            <>
              <p
                className={`text-sm font-semibold ${
                  status === 'locked' ? 'text-blue-900' : 'text-blue-600'
                }`}>
                {tempDiv.textContent}
              </p>
              <p className="text-xs text-gray-500">{filename}</p>
              {suggestions?.groupingVariables?.map((gv: string[], i: number) => (
                <p className="text-xs text-blue-400" key={i}>
                  {gv}
                </p>
              ))}
            </>
          ),
          value: i,
          nodeId: Object.keys(s)[0],
          order: suggestions?.order,
        };
      });
      return aiSuggestions;
    },
    [sourceDocuments],
  );

  useEffect(() => {
    setSearchTerm('');
  }, [aiSuggestionList, setSearchTerm]);

  const suggestionOptions = useMemo(() => {
    const lockedAiSuggestionsList: AiSuggestionListItem[] = [];
    const unlockedAiSuggestionsList: AiSuggestionListItem[] = [];

    aiSuggestionList?.forEach((item) => {
      const entry = Object.values(item)[0] as AiSuggestionEntryData | undefined;
      const sourceFileId = entry?.sourceFileId;
      const listItem = item as AiSuggestionListItem;
      sourceFileId === selectedCatalogId
        ? lockedAiSuggestionsList.push(listItem)
        : unlockedAiSuggestionsList.push(listItem);
    });

    const aiLockedSuggestions = lockedAiSuggestionsList.length
      ? options(lockedAiSuggestionsList, 'locked')
      : [];
    const aiUnlockedSuggestions =
      !lockSourceFile && unlockedAiSuggestionsList.length
        ? options(unlockedAiSuggestionsList, 'unlocked')
        : [];
    const availableSuggestions = [...aiLockedSuggestions, ...aiUnlockedSuggestions];
    availableSuggestions.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return availableSuggestions;
  }, [aiSuggestionList, lockSourceFile, selectedCatalogId, options]);

  const customTextOnSelect = (node: customNode): string => {
    if (
      node &&
      node.props &&
      Array.isArray(node.props.children) &&
      node.props.children[0] &&
      node.props.children[0].props
    ) {
      return node.props.children[0].props.children.toString();
    }
    return '';
  };

  const navigateCells = (direction: 'next' | 'previous') => {
    removeSearchedHighlight();
    const nodeId = navigateToAuthoringCell(direction);
    if (nodeId) handleTargetNodeIds([getCurrentNodeId(nodeId)]);
  };

  const removeSearchedHighlight = () => {
    const prevEl = getElementsByQuery(editor2Ref, '.searched-source-node');
    if (prevEl && prevEl.length > 0) {
      (prevEl[0] as HTMLElement).classList.remove('searched-source-node');
      (prevEl[0] as HTMLElement).style.backgroundColor = '';
    }
  };

  useEffect(() => {
    setResetPlaceholder(true);
  }, [lockSourceFile, selectedCatalogId]);

  const handleSearchTerm = useCallback(
    async (term: string) => {
      let searchResponse: SearchResult[] = [];
      if (term) {
        const dataDocIds = lockSourceFile
          ? [+(sourceDocsArray.find((doc) => +doc.value === selectedCatalogId)?.value || 0)]
          : sourceDocsArray.map((doc) => +doc.value);

        searchResponse = await search({
          searchTerm: term,
          searchThreshold: SearchThreshold,
          dataDocIds: dataDocIds,
        }).then((result) => result.data.data);
      }
      if (!searchResponse.length || !term) {
        const results = [
          {
            label: (
              <>
                <p className="text-sm font-semibold  text-gray-500">No Result Found</p>
              </>
            ),
            value: -1,
          },
        ];
        setSearchOptions(results);
      } else {
        setSearchResult(searchResponse);
        const options: SearchOptions[] = searchResponse?.map((result: SearchResult, i: number) => {
          return {
            label: (
              <div onClick={() => setIsSearched(true)}>
                <p className="text-sm font-semibold">{result.dataText}</p>
                <p className="text-xs text-gray-500">{result.fileTitle}</p>
              </div>
            ),
            value: i,
            nodeId: result.dataNodeId,
          };
        });
        setSearchOptions(options);
      }
    },
    [lockSourceFile, selectedCatalogId, setIsSearched, setSearchResult, sourceDocsArray],
  );

  const customUndoCallback = (e: KeyboardEvent) => {
    const isCustomUndo = (e.ctrlKey || e.metaKey) && e.key === 'e';
    const isDefaultUndo = (e.ctrlKey || e.metaKey) && e.key === 'z';

    if ((isCustomUndo || isDefaultUndo) && !isUndoEnabledRef.current) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return;
    }
    if (isCustomUndo && isUndoEnabledRef.current) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      undoButtonRef?.current?.click();
    }
  };

  const scrollToFirstHighlight = (editorRef: EditorRefObject) => {
    const editorElement = editorRef.current?.getDoc();
    const partialSelectionActiveSpan = editorElement?.querySelector('[data-highlight="active"]');

    if (editorElement && !partialSelectionActiveSpan) {
      const highlightedElement: HTMLElement | null = editorElement.querySelector(
        '[data-highlighted="true"]',
      );
      if (highlightedElement) {
        const eleNodeId = highlightedElement.getAttribute('data-nodeid');
        const currentNodeId = eleNodeId && getCurrentNodeId(eleNodeId, editorRef);
        if (!currentTargetNodeIds.length && currentNodeId) {
          handleTargetNodeIds([currentNodeId]);
          setColors([LIGHT_BLUE_COLOR]);
        }
      }
    }
  };

  const sourceFiles = Array.from(sourceDocuments?.entries() ?? [], ([_, source]) => ({
    title: source.filename,
    veevaNumber: source.veevaNumber,
  }));

  useEffect(() => {
    setSearchTerm('');
    const handleCustomUndo = (e: KeyboardEvent) => customUndoCallback(e);
    const handleEnterKey = (e: KeyboardEvent) => handleEnterKeyPress(e, acceptButtonRef);

    document.addEventListener('keypress', handleCustomUndo);
    document.addEventListener('keypress', handleEnterKey);

    return () => {
      document.removeEventListener('keypress', handleCustomUndo);
      document.removeEventListener('keypress', handleEnterKey);
    };
  }, [setSearchTerm]);

  const handleNewNodesOnEnterKeyPress = (e: KeyboardEvent | EditorEvent, editor: TinyMCEEditor) => {
    const { key, shiftKey } = e as KeyboardEvent;
    const generatedDocumentId = generatedDocumentRef?.current?.id?.toString();

    if (key === 'Enter' && !shiftKey && generatedDocumentId) {
      const currentAnchorNode = editor.selection.getSel()?.anchorNode as HTMLElement | null;
      const currentSelection =
        currentAnchorNode?.nodeName === '#text'
          ? (currentAnchorNode?.parentNode as HTMLElement | null)
          : currentAnchorNode;

      if (currentSelection && currentSelection.nodeName !== '#text') {
        const selectionNodeId = currentSelection?.getAttribute('data-nodeid');
        let nodes = null;

        // selectionNodeId can be null for new paragraphs inserted by Tinymce on enter/return
        if (selectionNodeId) {
          nodes = editor.getDoc().querySelectorAll(`[data-nodeid='${selectionNodeId}']`);
        }

        // If a new node is added with the duplicate attributes including data-nodeid, we can have more than one node
        if ((nodes && nodes.length > 1) || !selectionNodeId) {
          currentSelection.setAttribute('data-newnode', 'true');
          setAttributesAndOrder(currentSelection, generatedDocumentId, true);

          if (currentSelection.getAttribute('data-newnode')) {
            const newNodeId = currentSelection.getAttribute('data-nodeid');
            newNodeId && handleTargetNodeIds([newNodeId]);
          }
        }
      }
    }
  };

  const handleNewNodesOnTabKeyPress = (e: KeyboardEvent | EditorEvent, editor: TinyMCEEditor) => {
    const { key } = e as KeyboardEvent;
    const generatedDocumentId = generatedDocumentRef?.current?.id?.toString();
    if (key === 'Tab' && generatedDocumentId) {
      const currentNode = editor.selection.getNode();
      if (currentNode.tagName === 'LI') {
        const parentNode = currentNode.parentElement;
        if (!parentNode) return;

        const nestedNodeId = parentNode.getAttribute('data-nodeid');
        let closestParentNodeId: string | null = null;

        if (parentNode.tagName === 'OL' || parentNode.tagName === 'UL') {
          closestParentNodeId =
            parentNode.closest(parentNode.tagName.toLowerCase())?.getAttribute('data-nodeid') ??
            null;
        }

        if (closestParentNodeId && nestedNodeId && closestParentNodeId === nestedNodeId) {
          parentNode.removeAttribute('data-nodeid');
          setAttributesAndOrder(parentNode, generatedDocumentId, true);
          parentNode.setAttribute('data-newnode', 'true');
        }
      }
    }
  };

  const targetEventHandlers: TargetEditorEventHandler[] = [
    {
      event: 'keydown',
      handler: (e: KeyboardEvent | EditorEvent, editor: TinyMCEEditor) => {
        handleNewNodesOnEnterKeyPress(e, editor);
        handleKeydown(e as KeyboardEvent, acceptButtonRef, editor1Ref);
      },
    },
    {
      event: 'keyup',
      handler: (e: KeyboardEvent | EditorEvent, editor: TinyMCEEditor) => {
        handleNewNodesOnEnterKeyPress(e, editor);
        handleKeyUp(e as KeyboardEvent);
        handleNewNodesOnTabKeyPress(e, editor);
        handleEditorChange();
      },
    },
    {
      event: 'ExecCommand',
      handler: (e: KeyboardEvent | EditorEvent, editor: TinyMCEEditor) => {
        setMergedCellAttributesWhenNeeded(e as EditorEvent, editor);
        handleExecCommand(e as EditorEvent, editor, generatedDocumentRef);
        handleEditorChange();
      },
    },
    {
      event: 'keydown',
      handler: (e, _editor) => {
        const keyEvent = e as KeyboardEventWithShift;
        if (
          (keyEvent.metaKey || keyEvent.ctrlKey) &&
          keyEvent.key === 'g' &&
          isHeaderCellSelectedRef.current
        ) {
          openQuickFillModal();
        }
      },
    },
  ];

  const exportDocument = (_annotated = false) => {
    const innerHtml = editor1Ref.current?.getBody()?.innerHTML;
    const filename = generatedDocument?.filename;
    if (!innerHtml || !filename) return;

    handleDownload(
      innerHtml,
      filename,
      // suggestionsWithColors(),
      // mappingComments,
      // annotated,
      exportCleanDocument.mutateAsync,
      targetId,
      (id) => getFileFromS3.mutateAsync({ id }),
      sourceFiles,
    );
  };

  const handleClose = () => {
    if (targetChangesStatus === 'unsaved') openSaveChangesModal();
    else navigate('/catalog');
  };

  const handleAuthoringSaveChanges = async () => {
    await handleSave(undefined, AcceptanceMethodEnum.AUTHOR);
    unmountSavedRef.current = true;
    navigate('/catalog');
  };

  const handleAutoFull = async () => {
    if (isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR)) {
      setIsAutofill(true);
      await mappingSessionStore.handleAutoFill();
      setIsAutofill(false);
    } else {
      autoFillHandler();
    }
  };

  const actionHandlers = {
    [actionOptions.quickFill.value]: openQuickFillModal,
    [actionOptions.link.value]: handleLinkNodes,
    [actionOptions.comment.value]: () => openCommentModalWithThread(currentTargetNodeIds[0]),
    [actionOptions.viewComments.value]: openCommentsSlideOver,
    [actionOptions.imageTagging.value]: toggleImageTagging,
    [actionOptions.reset.value]: handleReset,
    [actionOptions.duplicateTable.value]: handleDuplicateTable,
    [actionOptions.transferTable.value]: handleTransferTable,
    [exportOptions.cleanDocument.value]: () => exportDocument(false),
    [exportOptions.annotatedDocument.value]: () => exportDocument(true),
    [actionOptions.autoFill.value]: handleAutoFull,
    [actionOptions.findFigure.value]: () => openFindFigureModal(),
    [actionOptions.viewOrignalTemplate.value]: () => handlePreviewDocument(baseTargetFileId),
  };

  const handleActionSelect = (option: OptionType) => {
    const handler = actionHandlers[option];
    if (handler) {
      handler();
    } else {
      console.error('Unknown option selected:', option);
    }
  };

  const handleModeChange = useCallback(
    (option: string) => {
      if (option === 'Editing Mode') {
        mappingSessionStore.setEditorMode(EditorMode.EDITING);
        setEditMode(true);
        !editMode && handleReset();
      } else {
        mappingSessionStore.setEditorMode(EditorMode.TRANSFER);
        setEditMode(false);
      }
    },
    [setEditMode, handleReset, editMode, mappingSessionStore],
  );
  useEffect(() => {
    const handleBeforeUnload = (event: { preventDefault: () => void; returnValue: string }) => {
      if (targetChangesStatus === TargetSaveState.UNSAVED) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [targetChangesStatus]);

  const handleSmartSourceToggle = (option: DropDownOption<SourceVersion>) => {
    if (isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR)) {
      const sourceFileId = mappingSessionStore.sourceFileId;
      if (!sourceFileId) {
        return;
      }
      mappingSessionStore.setSourceFileId(sourceFileId, option.value === 'Original');
    } else {
      handleSourceToggle();
    }
  };

  const handleSmartSaveChanges = async (
    editorType: SmartEditorType = 'target',
    acceptanceMethod: AcceptanceMethodEnum = AcceptanceMethodEnum.MANUAL,
  ) => {
    if (!isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR)) {
      return handleSave(undefined, acceptanceMethod);
    } else {
      try {
        mappingSessionStore.setIsLoading(true);

        // Get suggestions directly in legacy format
        const updatedCells = await mappingSessionStore.handleSave(editorType);

        if (!hasPendingAuthoringSave(updatedCells)) {
          mappingSessionStore.setIsLoading(false);
          return { success: true as const, data: undefined };
        }

        // Call the save API
        const result = await callSaveSuggestions(
          updatedCells,
          acceptanceMethod,
          false, // addToHistory = false (already handled internally)
        );

        // Handle successful save
        if (result) {
          // Update baseline to new saved state
          mappingSessionStore.updateBaselineAfterSave(editorType);

          // Update UI status
          setTargetChangesStatus(TargetSaveState.SAVED);

          // Update accepted suggestions
          mappingSessionStore.setAcceptedSuggestions(result);
          mappingSessionStore.setIsLoading(false);

          return { success: true as const, data: undefined };
        }

        mappingSessionStore.setIsLoading(false);
        return { success: false as const, error: 'Save failed - no result returned' };
      } catch (error) {
        mappingSessionStore.setIsLoading(false);
        console.error('Save failed:', error);
        setTargetChangesStatus(TargetSaveState.UNSAVED);
        return { success: false as const, error };
      }
    }
  };

  // Keep refs in sync with latest values so cleanup can use them without stale closures.
  useEffect(() => {
    targetChangesStatusRef.current = targetChangesStatus;
  }, [targetChangesStatus]);

  useEffect(() => {
    handleSmartSaveRef.current = handleSmartSaveChanges;
  });

  // Autosave on unmount — fires when the user navigates away via the nav pane or any route change.
  useEffect(() => {
    return () => {
      if (!unmountSavedRef.current && targetChangesStatusRef.current === TargetSaveState.UNSAVED) {
        handleSmartSaveRef.current?.('target', AcceptanceMethodEnum.AUTO_SAVE);
      }
    };
  }, []);

  return (
    <>
      <Layout>
        <Layout.Header>
          <AuthoringHeader
            isAutoMapping={isAutoMapping}
            isLoading={isSaving || isSavingSuggestions || isSaveButtonLoading}
            handleSave={async () => {
              setIsSaveButtonLoading(true);
              try {
                const [result] = await Promise.all([
                  handleSmartSaveChanges('target', AcceptanceMethodEnum.MANUAL),
                  new Promise<void>((resolve) => setTimeout(resolve, 500)),
                ]);
                return result;
              } finally {
                setIsSaveButtonLoading(false);
              }
            }}
            handleClose={handleClose}
          />
        </Layout.Header>
        <Layout.Body>
          {isLoading ? (
            <LoadingOverlay />
          ) : (
            <div className="flex min-h-0 w-full flex-grow flex-col">
              {!isFullScreen && (
                <InfoPanel
                  lastSaved={lastSaved}
                  badges={[
                    { label: 'Authored By', values: authors },
                    { label: 'Status', values: [sessionStatus] },
                  ]}
                  activeUsers={activeUsers}
                />
              )}
              <div className="flex min-h-0 flex-grow flex-col">
                <AuthoringActions
                  suggestionOptions={suggestionOptions}
                  searchOptions={searchOptions}
                  isFullScreen={isFullScreen}
                  handleFullScreen={toggleFullScreen}
                  handleSearchTerm={handleSearchTerm}
                  customTextOnSelect={customTextOnSelect}
                  handleAiDropDownChange={handleAiDropDownChange}
                  targetChangesStatus={targetChangesStatus}
                  resetPlaceholder={resetPlaceholder}
                  setResetPlaceholder={setResetPlaceholder}
                  onModeChange={handleModeChange}
                  editMode={editMode}
                />
                {loadingSourceDocument && <LoadingOverlay />}
                <div className="w-full flex justify-between items-center space-x-5">
                  <div className="w-full">
                    <SourcesActions
                      navigateAddSource={() => {
                        navigate('/add-source', {
                          state: {
                            sessionId,
                            previousAction: AddSourcesPreviousAction.ADD_SOURCES,
                          },
                        });
                      }}
                      sourceFilesDropdown={sourceFilesDropdown}
                      lockedFileSwitch={lockedFileSwitch}
                      displayLockFileButton={true}
                      handleSourceToggle={(option: DropDownOption<SourceVersion>) =>
                        handleSmartSourceToggle(option)
                      }
                      currentSourceVersion={currentFileVersion}
                    />
                  </div>

                  <div className="w-full flex flex-col">
                    <div className="flex justify-start space-x-2">
                      <h2 className="text-primary-300 mb-2">Target:</h2>
                      <h3 className="text-primary-300 font-normal">
                        {generatedDocument?.filename}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-2 justify-between">
                      <div className="flex items-center space-x-2 whitespace-nowrap">
                        <Dropdown
                          customStyles={{
                            placeholder: { color: 'var(--neutral-400)' },
                            itemsWrapper: { zIndex: 100, width: '150%', maxHeight: '26rem' },
                          }}
                          dropdownIcon
                          onSelect={(option) => handleActionSelect(option.value)}
                          position="bottom"
                          type="button"
                          placeholder="Actions"
                          groupedOptions={groupedOptions(mappingType, {
                            isCommentEnabled: displayCommentButton,
                            isImageTaggingEnabled: !isSavingSuggestions && !editMode,
                            isLinkEnabled: !disableLinkButton && !editMode,
                            isAutoFillEnabled: !isSavingSuggestions && !editMode,
                            isResetEnabled: !editMode,
                            isFindFigureEnabled: isImageNodeSelected,
                            isDuplicateTableEnabled:
                              !isSavingSuggestions && !editMode && isDuplicateTableEnabled,
                            isTransferTableEnabled:
                              !isSavingSuggestions && !editMode && isTransferTableEnabled,
                          })}
                          menuType="divided"
                        />
                        <Observer>
                          {() => (
                            <Button
                              variant="primary"
                              ref={acceptButtonRef}
                              onClick={acceptSuggestionHandler}
                              disabled={
                                isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR)
                                  ? !mappingSessionStore.canTransfer
                                  : isDisabledApprove || isSavingSuggestions
                              }>
                              Transfer
                            </Button>
                          )}
                        </Observer>

                        {!isAutoMapping && (
                          <>
                            <Button
                              variant="primary"
                              className="!hidden"
                              ref={undoButtonRef}
                              disabled={!isUndoAvailable}
                              onClick={handleGeneralizationAuthoringUndo}>
                              Undo
                            </Button>
                          </>
                        )}
                        <SuggestionNavigateButtons
                          isPreviousAvailable={isPreviousAuthoringCellAvailable}
                          isNextAvailable={isNextAuthoringCellAvailable}
                          navigateMapping={navigateCells}
                        />
                      </div>
                      {isAiAuthoring && (
                        <div
                          className="flex items-center content-box-sizing space-x-3"
                          aria-hidden="true"
                          title="New Suggestions: hidden until the feature is finalized">
                          <ToggleSwitch
                            checked={isNewSuggestionsActive}
                            isDisabled={editMode}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              const { checked } = event.target;
                              handleReset();
                              setIsNewSuggestionsActive(checked);
                              if (!newSuggestionsVisited) {
                                setNewSuggestionsVisited(checked);
                                openNewSuggestionsModal();
                              }
                            }}
                          />
                          <h4 className="text-neutral-400 font-normal">New Suggestions</h4>
                          <div
                            onClick={openNewSuggestionsModal}
                            className="flex justify-center items-center px-[7px] text-sm font-bold rounded-full border-2 cursor-pointer text-primary-50 border-primary-50">
                            ?
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {!isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR) && (
                  <div
                    className="relative my-3 flex h-[calc(100dvh-13rem)] min-h-0 w-full min-w-0 flex-grow flex-row space-x-3"
                    data-testid="editor-wrapper">
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[4px] border border-neutral-200 p-3">
                      <TinyEditor
                        key="source"
                        editorType={EditorTypeEnum.Source}
                        disabled={isSavingSuggestions}
                        toolBar={authoringSourceToolbar}
                        pluginsOverride="powerpaste searchreplace table charmap advlist lists advtable"
                        editorOptions={(editor) => {
                          editor.on('keydown', (e: KeyboardEvent) => {
                            handleKeydown(e, acceptButtonRef, editor2Ref, true);
                          });
                          editor.on('keyup', handleKeyUp);
                        }}
                        initialize={() => {
                          highlightAllSourceDocumentSuggestions();
                        }}
                        onClickHandler={editMode ? undefined : sourceDocumentOnClickHandler}
                        height="100%"
                      />
                    </div>

                    <div
                      className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[4px] border border-neutral-200 p-3 ${
                        editMode ? 'bg-primary-50' : ''
                      } `}>
                      <TinyEditor
                        key="target"
                        editorType={EditorTypeEnum.Target}
                        disabled={isSavingSuggestions}
                        toolBar={authoringTargetToolbar}
                        editorOptions={(editor: TinyMCEEditor) => {
                          targetEventHandlers.forEach(({ event, handler }) => {
                            editor.on(event, (e: EditorEvent) => handler(e, editor));
                          });
                        }}
                        initialize={(evt: unknown, editor) => {
                          const initEvent = evt as { paste?: boolean };
                          highlightAllTargetSuggestions(editor1Ref);
                          if (!initEvent.paste && editor?.initialized && !editor.isDirty()) {
                            const doc = editor1Ref.current?.getDoc();
                            if (doc) setInitialTarget(deepCopyDocument(doc));
                          }
                        }}
                        onClickHandler={
                          editMode
                            ? undefined
                            : (event, editor) => {
                                targetOnClickHandler(event, editor);
                                const target = event.target as HTMLElement;
                                if (target.tagName !== 'IMG') {
                                  closeFindFigureModal();
                                }
                              }
                        }
                        scroll={() => {
                          if (!hasScrolledToFirstHighlight.current) {
                            hasScrolledToFirstHighlight.current = true;
                            scrollToFirstHighlight(editor1Ref);
                          }
                        }}
                        height="100%"
                      />
                    </div>
                  </div>
                )}

                {isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR) && (
                  <MappingSessionProvider store={mappingSessionStore}>
                    <Observer>
                      {() => (
                        <>
                          {mappingSessionStore.isLoading && <LoadingOverlay />}
                          {mappingSessionStore.error && (
                            <div>Error: {mappingSessionStore.error.message}</div>
                          )}
                          {!mappingSessionStore.error && (
                            <div
                              className="relative my-3 flex h-[calc(100dvh-13rem)] min-h-0 w-full min-w-0 flex-grow flex-row space-x-3"
                              data-testid="editor-wrapper2">
                              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[4px] border border-neutral-200 p-3">
                                <SmartEditor
                                  type="source"
                                  height="100%"
                                  content={mappingSessionStore.activeContent.get('source')}
                                />
                              </div>

                              <div
                                className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[4px] border border-neutral-200 p-3 ${
                                  mappingSessionStore.editorMode === EditorMode.EDITING
                                    ? 'bg-primary-50'
                                    : ''
                                } `}>
                                {!isEmpty(mappingSessionStore.activeContent.get('target')) && (
                                  <SmartEditor
                                    type="target"
                                    height="100%"
                                    content={mappingSessionStore.activeContent.get('target')}
                                    updateDropdownSelection={updateDropdownSelection}
                                  />
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </Observer>
                  </MappingSessionProvider>
                )}
              </div>
            </div>
          )}
          <DeleteConfirmationDialogue
            isOpen={showSourceDeleteModal}
            handleClose={handleFileRemoveModalClose}
            item="file"
            handleDelete={handleRemoveSourceFile}
          />
        </Layout.Body>
      </Layout>
      <ImageTagging
        isOpen={isTaggingSlideOverOpen}
        onClose={handleClosePinningSlideOver}
        handleTag={(formData) => handleSubmitTag(formData)}
        initialFormValues={imageTagFormValues}
      />
      {isModalOpen && (
        <FileNameModal
          isOpen={isModalOpen}
          documentTitle=""
          closeModal={() => setIsModalOpen(false)}
          mappingSessionId={sessionId}
        />
      )}
      {targetChangesStatus === TargetSaveState.UNSAVED && (
        <SaveChangesModal
          handleSaveChanges={handleAuthoringSaveChanges}
          handleQuit={() => navigate('/catalog')}
          isOpen={showSaveChangesModal}
          handleClose={closeSaveChangesModal}
        />
      )}
      {isQuickFillModalOpen && (
        <QuickFillModal
          quickFillModalOpen={isQuickFillModalOpen}
          closeQuickFillModal={closeQuickFillModal}
          gvCategories={gvCategories}
          clearSelection={clearSelection}
          selectedOption={selectedOption}
          handleOptionChange={handleOptionChange}
          handleApplyFilter={handleApplyFilter}
          isLoading={isSavingFilledSuggestions}
        />
      )}
      {isFindFigureModalOpen && (
        <FindFigureModal
          isOpen={isFindFigureModalOpen}
          handleClose={closeFindFigureModal}
          imageSuggestions={imageSuggestions}
          imageSuggestionsFiles={imageSuggestionsFiles}
          targetImageNodeId={currentTargetNodeIds[0]}
          handleSourceNodeIds={handleSourceNodeIds}
          isSavingSuggestions={isSavingSuggestions}
        />
      )}
      {newOrignalTemplateModal && selectedId && (
        <TinyMceModal
          closeEditorModalOpen={closeOrignalTemplateModal}
          editorModalOpen={newOrignalTemplateModal}
          key="catalog_preview"
          editorType="Session"
          catalogId={selectedId}
          isFileInteractive={true}
        />
      )}
      <NewSuggestionModal isOpen={newSuggestionsModal} handleClose={closeNewSuggestionsModal} />
      <CommentsSlideOver
        comments={mappingComments}
        isOpen={showCommentsSlideOver}
        onClose={closeCommentsSlideOver}
        onNavigateToTarget={scrollTargetToCommentNode}
      />
      {commentModal}
    </>
  );
};

export default Authoring;
